import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Wallet } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { formatEur, formatDate } from "@/lib/format";
import { imgSrc } from "@/components/ProposalDialog";

const ConnectCard = ({ t }) => {
  const { data } = useQuery({ queryKey: ["connect"], queryFn: async () => (await api.get("/connect/status")).data });
  const start = async () => {
    try { const { data: d } = await api.post("/connect/onboard", { origin_url: window.location.origin }); window.location.href = d.url; } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-5 rounded-2xl bg-[#EFE9FB] p-4" data-testid="connect-card">
      <p className="flex items-center gap-2 font-semibold text-[#3B1272]"><Wallet className="h-4 w-4" />{t("p3.orders.connectTitle")}</p>
      <p className="mt-1 text-sm text-[#4B4763]">{t("p3.orders.connectText")}</p>
      {data?.charges_enabled ? <p className="mt-2 text-sm font-semibold text-emerald-700" data-testid="connect-ok">{t("p3.orders.connectOk")}</p> : (
        <>{data?.connected && <p className="mt-2 text-xs text-amber-700">{t("p3.orders.connectPending")}</p>}
          <button onClick={start} className="btn-violet mt-3 !py-2 text-sm" data-testid="connect-onboard-btn">{t("p3.orders.connectBtn")}</button></>
      )}
    </div>
  );
};

const OrderRow = ({ o, role, t, locale, refresh }) => {
  const [tracking, setTracking] = useState("");
  const act = async (action) => {
    try { await api.post(`/orders/${o.order_id}/fulfil`, { action, tracking }); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-[#EEF0F6] p-4" data-testid={`order-${o.order_id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <img src={imgSrc(o.image)} alt="" className="h-14 w-14 rounded-xl object-cover" />
        <div className="min-w-0 flex-1"><p className="truncate font-semibold">{o.title}{o.qty > 1 && ` ×${o.qty}`}</p><p className="text-xs text-[#64748B]">{formatDate(o.created_at, locale, true)} · {t(`delivery.${o.delivery}`)}</p></div>
        {o.is_test && <span className="pill bg-[#1E1B2E] text-white">{t("p3.orders.test")}</span>}
        <span className="pill bg-[#EFE9FB] text-[#3B1272]" data-testid={`order-status-${o.order_id}`}>{t(`p3.orders.status.${o.status}`)}</span>
        <span className="font-display text-lg font-bold">{formatEur(o.total_cents, locale)}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 text-xs text-[#64748B]">
        {role === "buyer" && o.coins_used > 0 && <span>{t("p3.orders.coinsUsed")}: {o.coins_used} (−{formatEur(o.coin_discount_cents, locale)})</span>}
        {o.payment_fee_cents > 0 && <span>{t("p3.fee")}: {formatEur(o.payment_fee_cents, locale)}</span>}
        {o.tracking && <span>{t("p3.orders.tracking")}: {o.tracking}</span>}
        {o.refunds?.length > 0 && <span>{t("p3.orders.refunds")}: {formatEur(o.refunded_cents, locale)}</span>}
      </div>
      {role === "seller" && o.status === "paid" && (
        <div className="mt-3 flex flex-wrap gap-2">
          {o.delivery === "shipping" ? <><input className="field !w-48" placeholder={t("p3.orders.tracking")} value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid={`order-tracking-${o.order_id}`} />
            <button onClick={() => act("shipped")} className="btn-violet !py-2 text-sm" data-testid={`order-ship-${o.order_id}`}>{t("p3.orders.ship")}</button></>
            : <button onClick={() => act("ready_for_pickup")} className="btn-violet !py-2 text-sm" data-testid={`order-pickup-${o.order_id}`}>{t("p3.orders.pickup")}</button>}
        </div>
      )}
      {role === "buyer" && ["shipped", "ready_for_pickup"].includes(o.status) && <button onClick={() => act("delivered")} className="btn-violet mt-3 !py-2 text-sm" data-testid={`order-received-${o.order_id}`}>{t("p3.orders.received")}</button>}
    </div>
  );
};

export const OrdersTab = () => {
  const { t, locale } = useI18n();
  const [role, setRole] = useState("buyer");
  const { data = [], refetch } = useQuery({ queryKey: ["orders", role], queryFn: async () => (await api.get(`/orders?role=${role}`)).data });
  return (
    <div>
      {role === "seller" && <ConnectCard t={t} />}
      <div className="mb-4 flex gap-2">{["buyer", "seller"].map((r) => <button key={r} onClick={() => setRole(r)} data-testid={`orders-role-${r}`} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${role === r ? "bg-[#3B1272] text-white" : "bg-[#F1F4F9]"}`}>{t(`p3.orders.${r}`)}</button>)}</div>
      {!data.length ? <p className="rounded-2xl bg-[#F8F9FC] px-5 py-10 text-center text-sm text-[#64748B]" data-testid="orders-empty">{t("p3.orders.empty")}</p>
        : <div className="space-y-3">{data.map((o) => <OrderRow key={o.order_id} o={o} role={role} t={t} locale={locale} refresh={refetch} />)}</div>}
    </div>
  );
};

export const LedgerList = ({ entries, t, locale }) => (
  <div className="divide-y divide-[#EEF0F6] rounded-2xl border border-[#EEF0F6]" data-testid="wallet-ledger">
    {entries.map((e) => (
      <div key={e.entry_id} className="flex items-center justify-between px-4 py-2.5 text-sm">
        <span>{t(`p3.wallet.types.${e.type}`)}{e.order_id && <span className="ml-2 text-xs text-[#94A3B8]">{e.order_id}</span>}</span>
        <span className="text-right"><b>{["spent", "reversed", "reserved"].includes(e.type) ? "−" : "+"}{e.amount}</b><span className="ml-3 text-xs text-[#94A3B8]">{formatDate(e.created_at, locale, true)}</span></span>
      </div>
    ))}
  </div>
);
